<template>
  <textarea
    v-if="isTextArea"
    ref="inputRef"
    v-model="text"
    :name="name"
    :placeholder="placeholder"
    :maxlength="maxLength"
    :class="[props.context!.classes.textarea, 'pattern', small]"
    :data-testid="props.context.attrs['data-testid'] "
    @input="onInput"
    @change="() => onChange?.(text)"/>
  <input
    v-else
    ref="inputRef"
    v-model="text"
    :name="name"
    :placeholder="placeholder"
    :maxlength="maxLength"
    :class="[props.context!.classes.input, 'pattern', small]"
    :data-testid="props.context.attrs['data-testid'] "
    @input="onInput"
    @change="() => onChange?.(text)"/>
  <FormKitMessages :node="inputRef?.node"/>
</template>

<script setup lang="ts">
import { FormKitFrameworkContext } from '@formkit/core';
import { FormKitMessages } from '@formkit/vue';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { FormKitPattern } from '@/types/interfaces/ValidationRule.interface';

const props = defineProps<{ context: FormKitFrameworkContext }>();

const { t } = useI18n();

const text = ref(props.context.value || '');
const name = ref(props.context.node.name || '');
const onChange = (props.context.onChange) as ((value: string) => void) | undefined;
const required = ref(props.context.state.required ?? false);
const inputType = ref(props.context.type as 'TextPatternInput' | 'TextPatternTextArea');
const small = ref(props.context.small !== undefined ? 'small' : '');
const pattern = ref(props.context.pattern as FormKitPattern | undefined);
const regexp = ref(pattern.value?.expression);
const maxLength = ref(pattern.value?.maxlength);
const lowerCased = ref(pattern.value?.lowercased);
const upperCased = ref(pattern.value?.uppercased);
const transform = ref(pattern.value?.transform);
const placeholder = computed(() => {
  if (required.value) {
    return props.context.placeholder as string || '';
  }

  return props.context.placeholder as string || t('placeholder.input.optional');
});

const inputRef = ref();

const isTextArea = computed(() => inputType.value === 'TextPatternTextArea');

function transformValue(updated: string) {
  const replaced = regexp.value ? updated.replace(regexp.value, '') : updated;
  const transformed = transform.value ? transform.value(replaced) : replaced;
  const lowerCasedValue = lowerCased.value ? transformed.toLowerCase() : transformed;
  return upperCased.value ? lowerCasedValue.toUpperCase() : lowerCasedValue;
}

function onInput(event: Event) {
  const input = event.target as HTMLInputElement;
  const start = input.selectionStart;
  const end = input.selectionEnd;

  const newValue = transformValue(input.value);

  props.context.node.input(newValue);
  text.value = newValue;

  requestAnimationFrame(() => {
    input.setSelectionRange(start, end);
  });
}

watch(() => props.context.value, (newVal) => {
  text.value = newVal;
});
</script>
